import { memo } from '@tanstack/react-table';
import { type RefCallback } from 'react';

import { isDev } from '../../../core/utils/is-node-environment.js';
import { useDataTableV2ConfigElement } from '../../hooks/useDataTableV2Config.js';
import {
  type TableOptions,
  type Cell,
  type Column,
  type Header,
  type InitialTableState,
  type Row,
  type Table,
  type TableFeature,
  type ColumnDef,
} from '../hooks/useTable/types.js';
import { getMemoOptions } from '../hooks/useTable/utils.js';
import type { DataTableV2RowData } from '../public.api.js';
import { isRowSelectionColumnDef } from './RowSelection/RowSelection.js';

/**
 * Defines the shape of the `DataTableV2LayoutMeasuredColumnSizeState` state within the tanstack tables state.
 * @public
 */
export type DataTableV2LayoutColumnMeasureInfoState = Record<
  string,
  { width: number; height: number }
>;

/**
 * @internal
 * Extension interface to extend the tanstack table state with the LineWrap state.
 */
export interface DataTableV2LayoutTableState {
  columnMeasureInfo: DataTableV2LayoutColumnMeasureInfoState;
}

/**
 * @internal
 * Extension interface to extend the types of the `Column` from the tanstack table.
 */
export interface DataTableV2LayoutRow {
  getGridRowPosition: (rowIndex: number) => {
    gridColumnStart: number;
    gridColumnEnd: number | `span ${number}` | 'row-end';
    gridRowStart: number;
    gridRowEnd: number;
  };
}

/**
 * @internal
 * Extension interface to extend the types of the `Column` from the tanstack table.
 */
export interface DataTableV2LayoutColumn {
  getGridColumnPosition: () => {
    gridColumnStart: number | `span ${number}`;
    gridColumnEnd: number | `span ${number}`;
  };
}

/**
 * @internal
 * Extension interface to extend the types of the `HeaderCell` from the tanstack table.
 */
export interface DataTableV2LayoutHeader {
  getGridPosition: (columnIndex: number) => {
    gridColumnStart: number;
    gridColumnEnd: number | `span ${number}`;
    gridRowStart: number;
    gridRowEnd: number | `span ${number}`;
  };

  // Gets the measureRef that can be bound onto a cell
  // in order to get an automatic content width.
  getMeasureRef: () => RefCallback<HTMLElement> | undefined;
}

/**
 * @internal
 * Extension interface to extend the types of the `Cell` from the tanstack table.
 */
export interface DataTableV2LayoutCell {
  getGridPosition: (position: `${number}/${number}`) => {
    gridColumnStart: number;
    gridColumnEnd: number;
    gridRowStart: number;
    gridRowEnd: number;
  };
}

/**
 * @internal
 * Extension interface to extend the types of the columnDefinition from the tanstack table.
 */
export interface DataTableV2LayoutColumnDef {
  minWidth?: number;

  /**
   * Defines the preferred width of a column.
   * Options number | `${number}fr` | 'auto' | 'content'
   * - number: Defines the width of a column in pixels
   * - `${number}fr`: Lets you define a fractioned width. Child columns with fractions can split their fractions between the parents fraction.
   * - auto: Will distribute the space evenly between the auto columns. Leverages the auto sizing feature of a CSS grid
   * - content: Will look at content visible in the first render and size the columns according to their max-content.
   * - object with type and maxWidth: this maxWidth is the initial maxWidth and if resizing is enabled then you can resize beyond this maxWidth.
   */
  width?:
    | number
    | `${number}fr`
    | 'auto'
    | 'content'
    | {
        type: 'auto' | 'content';
        /**
         * Initial maximum width of the column. If resizing is enabled,
         * it is possible to resize beyond this maxWidth.
         */
        maxWidth?: number;
      };
  /**
   * Absolute maximum width of the column.
   */
  maxWidth?: number;
}

/**
 * @internal
 * Extension interface to extend the types from the tanstack table instance.
 */
export interface DataTableV2LayoutInstance {
  /**
   * Resets saved measurements so they will be remeasured.
   */
  resetGridMeasurements: () => void;

  /**
   * Creates the grid template rows for the table header, based on the number of header groups.
   */
  getHeaderGridTemplateRows: () => string;

  /**
   * Creates the grid template columns based on the specified widths in the column defintion.
   */
  getTableGridTemplate: (
    overrideHeaderSizes?: Record<string, { width?: number; height?: number }>,
  ) => string;

  /**
   * Evaluates if any element in the grid template has a fractional unit in there
   * as this will drive some feature decisions that are mutually exclusive.
   */
  _getHasFractionalTemplate: () => boolean;

  /**
   * Allows for the registration of a header element to to be measured.
   */
  _monitorHeader: (element: HTMLElement, columnId: string) => void;

  _getHeaderSize: (columnId: string) => { width: number; height: number };

  _setHeaderSize: (
    columnId: string,
    sizing: { width: number; height: number },
  ) => void;

  _getTotalHeaderHeight: () => number;

  _estimateRowHeight: (isHeaderRow?: boolean) => number;

  _resetHeaderMeasurement: (columnId: string) => void;

  /**
   * Returns the number of currently visible header rows.
   */
  _getVisibleHeaderGroupsCount: () => number;

  /**
   * Returns the number of prefix columns.
   */
  getPrefixColumnsCount: () => number;
}

/**
 * @internal
 * Extension interface to extend the types from the tanstack table options.
 */
export interface DataTableV2LayoutOptions {
  layout?: {
    fullWidth?: boolean;
    fullHeight?: boolean;
  };
}

/**
 * @internal
 * Typeguard function to make sure a passed width is a fraction width.
 */
export function isFractionWidth(
  width?:
    | number
    | `${number}fr`
    | 'auto'
    | 'content'
    | {
        type: 'auto' | 'content';
        maxWidth?: number;
      },
): width is `${number}fr` {
  return typeof width === 'string' && width.endsWith('fr');
}

/**
 * @internal
 * Typeguard function to make sure a passed width is an extended config.
 */

function isWidthExtendedConfig(
  width?:
    | number
    | `${number}fr`
    | 'auto'
    | 'content'
    | {
        type: 'auto' | 'content';
        maxWidth?: number;
      },
): width is {
  type: 'auto' | 'content';
  maxWidth?: number;
} {
  if (
    width !== undefined &&
    typeof width !== 'number' &&
    typeof width !== 'string'
  ) {
    return true;
  }

  return false;
}

export function useLayout<TData>(
  props: { fullWidth?: boolean; fullHeight?: boolean },
  options: TableOptions<TData>,
) {
  const configFullWidth = useDataTableV2ConfigElement('fullWidth');
  const configFullHeight = useDataTableV2ConfigElement('fullHeight');

  options.layout = {
    ...options.layout,
    fullWidth: props.fullWidth ?? configFullWidth,
    fullHeight: props.fullHeight ?? configFullHeight,
  };
}

export const SPACER_COLUMN: ColumnDef<unknown> = {
  id: 'DataTableV2SpacerColumn',
  header: () => undefined,
  cell: () => undefined,
  enableSorting: false,
  enablePinning: false,
  enableGrouping: false,
  enableUserActions: false,
  enableResizing: false,
  enableHiding: false,
  meta: {
    builtin: true,
    builtinPlacement: 'suffix',
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
    spacer: true,
  },
};

export const Layout: TableFeature = {
  getInitialState(state?: InitialTableState) {
    return {
      columnMeasureInfo: {},
      ...state,
    };
  },

  createHeader<TData extends DataTableV2RowData, TValue>(
    header: Header<TData, TValue>,
    table: Table<TData>,
  ) {
    /**
     * Allows to get the gridPosition of a specific header cell in order
     * to place it into the correct place in the template and span it across the correct
     * elements.
     */
    header.getGridPosition = memo(
      () => [header.headerGroup.headers],
      (headers) => {
        // Get the start of the column index the header should be rendered into,
        // this should be equal to the index of the header cell in the loop passed
        // to this function.
        let gridColumnStart = 0;
        for (const headerInGroup of headers) {
          if (headerInGroup === header) {
            break;
          }
          gridColumnStart = gridColumnStart + headerInGroup.colSpan;
        }

        // The header already exposes a colSpan value, which can be used to span
        // the header cell element from the start position to the end of the
        // header group.
        const gridColumnEnd = `span ${header.colSpan}` as `span ${number}`;

        // Get the start of the index as this is actually the defined
        // column nesting level. If the column is not nested, it will get a
        // depth of 0, and nesting levels increase going deeper.
        const gridRowStart = header.column.depth;

        // Get the end from the headers depth, as this is where it would be rendered
        // within the tanstack table. As nested columns would be rendered in the
        // lower headerrow. The strato approach here is different, as the header
        // cell should span both header rows
        const gridRowEnd = header.depth;

        // return the gridColumn positioning. As the grid column positioning in
        // css is One-based, it needs an offset of 1 in every entry.
        return {
          gridColumnStart: gridColumnStart + 1,
          gridColumnEnd,
          gridRowStart: gridRowStart + 1,
          gridRowEnd: `span ${gridRowEnd - gridRowStart}`,
        };
      },
      getMemoOptions(table.options, 'debugHeaders', 'header.getGridPosition'),
    );

    /**
     * Gets a callback ref to keep the header sizes measured.
     */
    header.getMeasureRef = memo(
      // The dependency should align with the check on how many columns the measurement
      // should happen.
      () => [table.getAllFlatColumns()],
      () => {
        return (element: HTMLElement) => {
          if (element) {
            table._monitorHeader(element, header.column.id);
          }
        };
      },
      getMemoOptions(table.options, 'debugHeaders', 'header.getMeasureRef'),
    );
  },

  createRow<TData extends DataTableV2RowData>(
    row: Row<TData>,
    table: Table<TData>,
  ) {
    row.getGridRowPosition = memo(
      (rowIndex) => [rowIndex],
      (rowIndex: number = -1) => {
        return {
          // Offset the index by one as the CSS grid column is a one based position.
          gridColumnStart: 1,
          // The row should span all columns.
          gridColumnEnd: 'row-end',
          // Offset the index by one as the CSS grid row is a one based position.
          gridRowStart: rowIndex + 1,
          // Offset the index by one as the CSS grid row is a one based position.
          gridRowEnd: rowIndex + 2,
        };
      },
      getMemoOptions(table.options, 'debugRows', 'row.getGridRowPosition'),
    );
  },

  createCell<TData extends DataTableV2RowData, TValue>(
    cell: Cell<TData, TValue>,
    column: Column<TData, TValue>,
    row: Row<TData>,
    table: Table<TData>,
  ) {
    cell.getGridPosition = memo(
      // getGridPosition needs to be memoized in order to return the same object instance every
      // time unless changed, in order for React.memo to work on the DataTableV2Cell.
      // This comes with some challenges, we can pass a parameter to memo, but it needs to be a trivial one,
      // as memo will do a === equality check on the depsArgs. It also can only be one argument, not multiple ones.
      // See @tanstack/table-core depArgs function and @tanstack/table-core comparison.
      // So in order to leverage the memoization here, we can change the parameter type from columnIndex: number, rowIndex: number to
      // template literal type string, that combines both parameters. This will make comparison easy on the memo and cache it properly.
      // - depArgs function: https://github.com/TanStack/table/blob/main/packages/table-core/src/utils.ts#L137
      // - comparison function: https://github.com/TanStack/table/blob/main/packages/table-core/src/utils.ts#L154-L156
      (position) => [position],
      (position?: `${number}/${number}`) => {
        const [columnIndex, rowIndex] = (position ?? '/').split('/');
        const { gridRowStart, gridRowEnd } = row.getGridRowPosition(
          parseInt(rowIndex),
        );

        return {
          // Offset the index by one as the CSS grid column is a one based position.
          gridColumnStart: parseInt(columnIndex) + 1,
          // Offset the index by one as the CSS grid column is a one based position.
          gridColumnEnd: parseInt(columnIndex) + 1,
          gridRowStart,
          gridRowEnd,
        };
      },
      getMemoOptions(table.options, 'debugCells', 'cell.getGridPosition'),
    );
  },

  createTable<TData extends DataTableV2RowData>(table: Table<TData>): void {
    const observedHeadersByElement = new Map<Element, string>();
    const observedHeadersByKey = new Map<string, Element>();
    const headerCellSizes = new Map<
      string,
      { width?: number; height: number }
    >();

    const headerCellObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const column = observedHeadersByElement.get(entry.target);
        // if currently the header is inside the column order drag overlay, do nothing
        if (table.getState().draggedHeaderColumn?.id === column) {
          return;
        }

        const width = entry.borderBoxSize.at(0)?.inlineSize;
        const height = entry.borderBoxSize.at(0)?.blockSize;
        if (column && width !== undefined && height !== undefined) {
          table._queue(() => {
            const columnDef = table.getColumn(column)?.columnDef;
            const isContentWidth =
              columnDef?.width === 'content' ||
              (isWidthExtendedConfig(columnDef?.width) &&
                columnDef.width.type === 'content');

            if (
              isContentWidth &&
              table._hasColumnCellsRendered(column) === false
            ) {
              // Once the headers are also in the virtualized content, this should no longer
              // be a problem. This entire header measurement should be able to be removed since we do not
              // want to commit a fixed width to a header that is still outside the virtualized area.
              return;
            }

            let adaptedWidth = width;

            // When the column has width 'content' the measured width needs to be rounded down due to number precision errors
            // with periodic numbers and the width is also increased by 1px to mitigate it being too narrow sometimes for bold fonts in Windows.
            if (!columnDef?.meta?.builtin && isContentWidth) {
              adaptedWidth = Math.floor(width) + 1;
            }

            const prevSize = headerCellSizes.get(column);
            // If the headers have an updated height or width (here the new width is actually adaptedWidth stored on the first measurement), we need to store new sizes and retrigger
            // a table render in order to update layout.
            if (prevSize?.height !== height || prevSize?.width !== width) {
              if (width !== 0 || height !== 0) {
                headerCellSizes.set(column, {
                  width: adaptedWidth,
                  height,
                });
              } else {
                // Delete existing entries if width and height are zero (e.g. if column was hidden)
                // This prevents faulty grid sizing when a column is then shown again
                headerCellSizes.delete(column);
                observedHeadersByElement.delete(entry.target);
                observedHeadersByKey.delete(column);
              }

              // Only call the state update when we are not resizing in order to keep performance stable.
              if (
                table.getState().columnSizingInfo.isResizingColumn === false
              ) {
                table.setState((state) => ({ ...state }));
              }
            }
          });
        }
      }
    });

    /**
     * Registers the passed element with the tables header cell resize observer
     * to keep track of the header width and height for virtualization and column sizing.
     */
    table._monitorHeader = (element: HTMLElement, columnId: string) => {
      if (!observedHeadersByElement.has(element)) {
        observedHeadersByElement.set(element, columnId);
        headerCellObserver.observe(element, { box: 'border-box' });
        observedHeadersByKey.set(columnId, element);
      }
    };

    /** Queue handler to avoid multiple set states in a row and excessive rerenders. */
    let remeasureQueued = false;

    /**
     * Removes the given columnId from the measurements map, which will let
     * the layout hook remeasure the actual size of the grid and apply the correct fixed width again.
     */
    table._resetHeaderMeasurement = (columnId: string) => {
      const savedCellSize = headerCellSizes.get(columnId);
      if (savedCellSize) {
        headerCellSizes.set(columnId, {
          width: undefined,
          height: savedCellSize.height,
        });

        // Force a re-render to remeasure.
        if (remeasureQueued === false) {
          remeasureQueued = true;
          table._queue(() => {
            remeasureQueued = false;
            table.setState((state) => ({ ...state }));
          });
        }
      }
    };

    /**
     * Removes all entries in the measurements map, which will let the layout hook remeasure the size of the grid/columns.
     */
    table.resetGridMeasurements = memo(
      () => [
        table.getRowModel().rows.length !== 0,
        // When the subrowIndicatorColumn moves to a different column because of columnVisibility changes
        // we need to make sure to remeasure the grid elements.
        table.getSubRowIndicatorColumnId(),
        table.getState().visualVariantConfig.rowDensity,
        table.options.columns,
      ],
      () => {
        headerCellSizes.clear();
        observedHeadersByElement.clear();
        // Force a re-render to remeasure.
        if (remeasureQueued === false) {
          remeasureQueued = true;
          table._queue(() => {
            remeasureQueued = false;
            table.setState((state) => ({ ...state }));
          });
        }
      },
      getMemoOptions(
        table.options,
        'debugTable',
        'table.resetGridMeasurements',
      ),
    );

    /**
     * Allows other features and template to access the headerSizes by columnId.
     */
    table._getHeaderSize = (
      columnId: string,
    ): {
      width: number;
      height: number;
    } => {
      const headerSize = table
        .getVirtualizationContainer()
        ?.querySelector(
          `[role="columnheader"][data-columnid=${CSS.escape(
            columnId,
          )}], [role="presentation"][data-columnid=${CSS.escape(columnId)}]`,
        )
        ?.getBoundingClientRect();

      if (headerSize === undefined) {
        // Use some standard value if no resize observer is there
        return {
          width: 150,
          height: table._estimateRowHeight(),
        };
      }

      return {
        width: headerSize.width,
        height: headerSize.height,
      };
    };

    /** Allows other features and template to store new headerSize by columnId. */
    table._setHeaderSize = (
      columnId: string,
      sizing: {
        width: number;
        height: number;
      },
    ): void => {
      const originalHeaderSize = headerCellSizes.get(columnId);
      if (originalHeaderSize) {
        const newSizing = {
          width: sizing.width ?? originalHeaderSize.width,
          height: sizing.height ?? originalHeaderSize.height,
        };
        headerCellSizes.set(columnId, newSizing);
        return;
      }

      headerCellSizes.set(columnId, {
        width: sizing.width,
        height: sizing.height,
      });
    };

    /**
     * Calculates the total size of all column groups combined.
     */
    table._getTotalHeaderHeight = memo(
      () => [
        table.getVisualVariantOptions(),
        table.getVisibleLeafColumnsWithoutBuiltin(),
        table.getHeaderGroups(),
        Array.from(headerCellSizes.entries())
          .map(([col, { height }]) => `${col}-${height}`)
          .join(),
      ],
      (visualVariantOptions, visibleLeafColumns, tableHeaderGroups) => {
        // If the headers are hidden, we can opt out earlier.
        if (
          visualVariantOptions.headers === 'hidden' ||
          visibleLeafColumns.length === 0 ||
          (tableHeaderGroups.length === 1 &&
            tableHeaderGroups.at(0)?.headers.length === 0)
        ) {
          return 0;
        }

        // Get the header container to measure its height
        const headerHeight = table
          .getVirtualizationContainer()
          ?.querySelectorAll('[role="rowgroup"]')[0]
          ?.getBoundingClientRect().height;

        // if the measured header height is still undefined or 0, estimate it based on row density and amount of header groups,
        // so that the rowVirtualizer can return the most accurate height possible for the first render
        // this prevents an unnecessary vertical scrollbar which would cause wrong column measurements on initial render
        if (!headerHeight) {
          // the header's bottom border is actually 2px, estimateRowHeight only considers 1px so we need to add 1px more
          const bottomBorderOffset = 1;
          const fallbackHeight =
            table._estimateRowHeight(true) *
              table._getVisibleHeaderGroupsCount() +
            bottomBorderOffset;

          return fallbackHeight;
        }

        return headerHeight;
      },
      {
        key: isDev() && '_getTotalHeaderHeight',
      },
    );

    table._getHasFractionalTemplate = memo(
      () => [table.getTableGridTemplate()],
      () => {
        const template = table.getTableGridTemplate();
        return template.includes('fr');
      },
      getMemoOptions(
        table.options,
        'debugTable',
        'table._getHasFractionalTemplate',
      ),
    );

    table._estimateRowHeight = memo(
      (isHeaderRow = false) => [isHeaderRow, table.getVisualVariantOptions()],
      (isHeaderRow, { rowDensity, rowSeparation }) => {
        // if rowSeparation is enabled, add 1px border; header rows always have a border
        const borderWidth =
          rowSeparation === 'none' && isHeaderRow === false ? 0 : 1;
        let rowHeight = 36;
        if (rowDensity === 'condensed') {
          rowHeight = 24;
        } else if (rowDensity === 'comfortable') {
          rowHeight = 44;
        }
        return rowHeight + borderWidth;
      },
      getMemoOptions(table.options, 'debugTable', 'table._estimateRowHeight'),
    );

    table._getVisibleHeaderGroupsCount = () => {
      if (table.getVisualVariantOptions().headers === 'hidden') {
        return 0;
      }

      return table.getHeaderGroups().length;
    };

    table.getHeaderGridTemplateRows = memo(
      () => [table._getVisibleHeaderGroupsCount()],
      (visibleHeaderGroupsCount) => {
        switch (visibleHeaderGroupsCount) {
          case 0:
            return '[body-start]';
          case 1:
            return '[header-start] max-content [header-end body-start]';
          default:
            return '[header-start] max-content max-content [header-end body-start]';
        }
      },
      getMemoOptions(
        table.options,
        'debugTable',
        'table.getHeaderGridTemplateRows',
      ),
    );

    table.getTableGridTemplate = memo(
      (
        overrideHeaderSizes?: Record<
          string,
          { width?: number; height?: number }
        >,
      ) => [
        table.getVisibleLeafColumns(),
        table.getState().columnSizing,
        table.getState().columnSizingInfo,
        table.getState().visualVariantConfig.rowDensity,
        overrideHeaderSizes,
        Array.from(headerCellSizes.entries())
          .map(([col, { width }]) => `${col}-${width}`)
          .join(),
      ],
      (
        allColumns,
        columnSizing,
        columnSizingInfo,
        rowDensity,
        overrideHeaderSizes,
      ) => {
        // TODO - When column pinning is implemented for the non-prefix columns
        // instead of table.getVisibleLeafColumns() we should check
        // table.getLeftLeafColumns(), table.getRightLeafColumns(), table.getCenterLeafColumns()
        // and based on that create grid template layout - APPDEV-12407
        if (
          allColumns === undefined ||
          columnSizing === undefined ||
          columnSizingInfo === undefined
        ) {
          return '';
        }

        // Proxy function that will allow the calling function to define overrides
        // for a newly applied grid template.
        function getHeaderSize(
          columnId: string,
        ): { width?: number; height?: number } | undefined {
          if (
            overrideHeaderSizes !== undefined &&
            overrideHeaderSizes[columnId] !== undefined
          ) {
            return overrideHeaderSizes[columnId];
          }
          return headerCellSizes.get(columnId);
        }

        // Here we want to deal first with initial sizing
        // So we create a grid layout, that is used as a default

        // But then read what the resizer considers as an update
        // this way, we would also be able to _reset_ to the original
        // size, if we want it to.
        const columnsTemplate: string[] = [];

        const pinnedRightColumnsTemplate: string[] = [];

        for (const column of allColumns) {
          const {
            minWidth = 30,
            width,
            maxWidth = Number.MAX_SAFE_INTEGER,
          } = column.columnDef;

          const pushTemplateDestination =
            column.getIsPinned() === 'right'
              ? pinnedRightColumnsTemplate
              : columnsTemplate;

          if (column.columnDef.meta?.builtinPlacement === 'prefix') {
            const isFirstColumn = column.getIsFirstColumn();
            const isRowThresholdEnabled =
              table.options.hasThresholds &&
              table.options.rowThresholds?.some(
                (threshold) => threshold.type === 'pill',
              );

            const ICON_WIDTH = 20;
            // icons in built-in columns have 2px padding left and right
            const iconPadding = isRowSelectionColumnDef(column.columnDef)
              ? 0
              : 4;
            const thresholdPillSize =
              isFirstColumn && isRowThresholdEnabled ? 4 : 0;

            let rowDensityPadding = 8;
            if (rowDensity === 'condensed') {
              rowDensityPadding = 2;
            } else if (rowDensity === 'comfortable') {
              rowDensityPadding = 12;
            }

            if (isFirstColumn) {
              rowDensityPadding *= 2;
            }

            const prefixColumnWidth =
              ICON_WIDTH + iconPadding + thresholdPillSize + rowDensityPadding;
            // prefix columns should never grow wider
            columnsTemplate.push(`${prefixColumnWidth}px`);
            continue;
          }

          if (column.columnDef.meta?.spacer === true) {
            columnsTemplate.push('auto');
            continue;
          }

          if (column.columnDef.meta?.builtinPlacement === 'suffix') {
            // row actions column should take minimum width
            columnsTemplate.push('min-content');
            continue;
          }

          // Check if the column is currently resizing
          if (columnSizingInfo.isResizingColumn === column.id) {
            columnsTemplate.push('var(--strato-resizing-size)');
            continue;
          }

          // Check if a column has a specific columnSize defined
          if (columnSizing[column.id] !== undefined) {
            pushTemplateDestination.push(
              `${Math.min(
                Math.max(columnSizing[column.id], minWidth),
                maxWidth,
              )}px`,
            );
            continue;
          }

          // If no width is defined, we define a layout fallback
          if (width === undefined) {
            // Consider the parent's width if it is defined
            if (
              column.parent &&
              typeof column.parent.columnDef.width === 'number'
            ) {
              const parentWidth = column.parent.columnDef.width;
              let totalDefinedChildWidths = 0;
              let undefinedWidthChildCount = 1;
              column.parent.getVisibleLeafColumns().forEach((childCol) => {
                const childWidth = childCol.columnDef.width;
                if (childWidth === undefined && childCol.id !== column.id) {
                  undefinedWidthChildCount++;
                } else if (typeof childWidth === 'number') {
                  totalDefinedChildWidths += childWidth;
                } else if (childWidth === 'auto' || childWidth === 'content') {
                  totalDefinedChildWidths +=
                    getHeaderSize(childCol.id)?.width ?? 0;
                } else if (isFractionWidth(childWidth) && isDev()) {
                  // eslint-disable-next-line no-console
                  console.warn(
                    'Fraction width is not allowed in combination with a fixed width parent column.',
                  );
                }
              });

              // subtract all known child column widths from the parent width and divide remaining width between remaining child cols
              const remainingWidth =
                (parentWidth - totalDefinedChildWidths) /
                undefinedWidthChildCount;

              pushTemplateDestination.push(`${remainingWidth}px`);
              continue;
            } else {
              const measurement = getHeaderSize(column.id);
              let columnTemplateValue = '1fr';
              // Remeasure widths and switch to fixed width instead of flex
              if (measurement?.width !== undefined) {
                columnTemplateValue = `${Math.max(
                  minWidth,
                  Math.min(measurement.width, maxWidth),
                )}px`;
              }

              pushTemplateDestination.push(columnTemplateValue);
              continue;
            }
          }

          // If the width is a number, we can assume this is a pixel value that it
          // wants to adjust for.
          if (typeof width === 'number') {
            pushTemplateDestination.push(
              `${Math.min(Math.max(width, minWidth), maxWidth)}px`,
            );
            continue;
          }

          if (typeof width === 'string' || isWidthExtendedConfig(width)) {
            // If the width definition is a fraction, we can use it as a
            // fraction layout value
            if (isFractionWidth(width)) {
              // If there is a parent column and it also has a fraction width
              // defined, we can calculate more accurate fractions for the
              // children columns based on the relationship between parent columns
              // fraction size and the current child columns size.
              if (
                column.parent &&
                isFractionWidth(column.parent.columnDef.width)
              ) {
                const parentFraction = parseFloat(
                  column.parent.columnDef.width,
                );
                const columnFraction = parseFloat(
                  column.columnDef.width as `${number}fr`,
                );

                const visibleSiblingColumns =
                  column.parent.getVisibleLeafColumns();
                const siblingColumnsSum = visibleSiblingColumns.reduce(
                  (sum, column) => {
                    if (isFractionWidth(column.columnDef.width)) {
                      return sum + parseFloat(column.columnDef.width);
                    }
                    return sum;
                  },
                  0,
                );

                const calculatedFraction =
                  parentFraction * (columnFraction / siblingColumnsSum);

                pushTemplateDestination.push(
                  // DISCUSS: Putting the fraction here might not be ideal here,
                  // as it does not clamp max-widths. But the current implementation
                  // of the DataTable layout also does not take this into account.
                  `${calculatedFraction}fr`,
                );

                continue;
              } else {
                pushTemplateDestination.push(`minmax(${minWidth}px, ${width})`);
                continue;
              }
            }

            // Set the width to `auto` and let the grid layout span the value
            if (
              width === 'auto' ||
              (isWidthExtendedConfig(width) && width.type === 'auto')
            ) {
              const measurement = getHeaderSize(column.id);
              let columnTemplateValue = 'minmax(min-content, auto)';

              let autoMaxWidth = maxWidth;
              if (isWidthExtendedConfig(width) && width.maxWidth) {
                autoMaxWidth = Math.min(maxWidth, width.maxWidth);
                columnTemplateValue = `minmax(auto, ${autoMaxWidth}px)`;
              }

              if (
                measurement !== undefined &&
                measurement.width !== undefined
              ) {
                columnTemplateValue = `${Math.max(
                  minWidth,
                  Math.min(measurement.width, autoMaxWidth),
                )}px`;
              }

              pushTemplateDestination.push(`${columnTemplateValue}`);
              continue;
            }

            // Set the width to `content` and let the grid layout span the value
            if (
              width === 'content' ||
              (isWidthExtendedConfig(width) && width.type === 'content')
            ) {
              const measurement = getHeaderSize(column.id);
              let columnTemplateValue = 'max-content';
              let contentMaxWidth = maxWidth;
              if (isWidthExtendedConfig(width) && width.maxWidth) {
                contentMaxWidth = Math.min(maxWidth, width.maxWidth);
                columnTemplateValue = 'max-content';
              }
              if (
                measurement !== undefined &&
                measurement.width !== undefined
              ) {
                columnTemplateValue = `${Math.max(
                  minWidth,
                  Math.min(measurement.width, contentMaxWidth),
                )}px`;
              }

              pushTemplateDestination.push(`${columnTemplateValue}`);
              continue;
            }
          }
        }

        const allColumnsTemplate = [
          ...columnsTemplate,
          ...pinnedRightColumnsTemplate,
        ];

        return `${allColumnsTemplate.join(' ')} [row-end]`;
      },
      getMemoOptions(table.options, 'debugTable', 'table.getTableGridTemplate'),
    );

    table.getPrefixColumnsCount = memo(
      () => [table.getAllFlatColumns()],
      (flatColumns) => {
        return flatColumns.filter(
          (column) => column.columnDef.meta?.builtinPlacement === 'prefix',
        ).length;
      },
      getMemoOptions(
        table.options,
        'debugHeaders',
        'table.getPrefixColumnsCount',
      ),
    );
  },
};
